import React, { useState } from "react";
import { Grid } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { CreateOrder, OrderList } from "@/types";
import { useAuth } from "@/contexts/AuthContext";
import { useTradeLock } from "@/hooks/useTradeLock";
import { postCreateOrder, useGetCreateOrderFromOrder, useGetOrderAmounts } from "@/services/Orders";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import { notifications } from "@mantine/notifications";
import { useGetCompanyCreditById } from "@/services/Company";
import { Controller, useForm } from "react-hook-form";
import Modal, { ModalCTAs } from "@/components/molecules/Modal";
import Drawer, { DrawerHeader } from "@/components/molecules/Drawer";
import OrderTypeEnum, { OrderTypeEnumString } from "@/constants/enums/OrderTypeEnum";
import AccordionDrawer from "@/components/organisms/AccordionDrawer";
import Text from "@/components/atoms/Text";
import CTA from "@/components/atoms/CTA";
import NumberFormatter from "@/components/atoms/NumberFormatter";
import Stack from "@/components/atoms/Stack";
import dayjs from "dayjs";
import Divider from "@/components/atoms/Divider";
import Select from "@/components/atoms/Select";
import Group from "@/components/atoms/Group";
import styles from "./styles.module.css";

interface OrderDrawerProps {
  order: OrderList | null;
  onClose: () => void;
}

const OrderDrawer = ({ order, onClose }: OrderDrawerProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user, currentCompany } = useAuth();
  const { guardTradeLock } = useTradeLock();

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const priceToPay = order ? order.priceWithFee?.[order.orderCurrency] : undefined;

  const { data: companyCreditData } = useGetCompanyCreditById(currentCompany?.companyId);
  const { data: amountsForOrderData } = useGetOrderAmounts(order?.id as number, { enabled: !!order?.id });
  const { data: createOppositeOrderTypeData } = useGetCreateOrderFromOrder(
    order?.id as number,
    currentCompany?.companyId as number,
    user?.id as number,
    priceToPay as number,
    { enabled: !!order?.id && !!currentCompany?.companyId && !!user?.id }
  );

  const {
    handleSubmit,
    control,
    getValues: getFormValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      amount: undefined,
    },
  });

  const { mutateAsync: mutateCreateOrder } = useMutation({
    mutationFn: async (data: CreateOrder) => {
      await postCreateOrder(data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getOrdersForCompany", currentCompany?.companyId],
      });
      notifications.show(SuccessNotificationObj(t("notifications:generic.success"), ""));
      onClose();
    },
    onError: () => {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.error"), t("notifications:orders.orderCreatedError"))
      );
    },
  });

  // Backstop: this is the single point where the trade is actually committed. The Buy/Sell CTA below is
  // guarded too, so a locked company never reaches the confirmation modal in the first place.
  const handleSellOrPurchase = guardTradeLock(async () => {
    if (!createOppositeOrderTypeData) return;
    if (createOppositeOrderTypeData?.orderType === OrderTypeEnum.BUY) {
      await handleBuy(createOppositeOrderTypeData);
    } else if (createOppositeOrderTypeData?.orderType === OrderTypeEnum.SELL) {
      await handleSale(createOppositeOrderTypeData);
    }
  });

  const handleBuy = async (orderCopy: CreateOrder) => {
    const amount = getFormValues("amount");
    if (!amount || !companyCreditData?.remainingCredit) return;
    const totalPrice = orderCopy.price * amount;

    if (companyCreditData?.remainingCredit < totalPrice) {
      notifications.show(ErrorNotificationObj(t("notifications:generic.error"), t("validation:remainingCreditTooLow")));
      return;
    }

    orderCopy.amount = amount;
    orderCopy.minimumAmount = amount;

    await mutateCreateOrder(orderCopy);
  };

  const handleSale = async (orderCopy: CreateOrder) => {
    // TODO: In old solution we had the user pick boxtypes and nrOfBoxes, this has been removed for now (also in wiz)
    await mutateCreateOrder(orderCopy);
  };

  // Early return kept below every hook call so the hook order stays stable as `order` toggles null.
  if (!order) return null;

  return (
    <>
      <Drawer
        isOpen={order !== null}
        onClose={onClose}
        title={
          order ? (
            <DrawerHeader
              title={order.salesItem.nameTranslated?.[user?.language.id as number] ?? order.salesItem.salesItem}
              svgName={order.orderType.type === "SELL" ? "salesIndicatorLarge" : "purchaseIndicatorLarge"}
            />
          ) : undefined
        }
        ctaRow={
          order && (
            <ModalCTAs>
              <form>
                <Stack>
                  {createOppositeOrderTypeData?.orderType === OrderTypeEnum.BUY ? (
                    <Controller
                      control={control}
                      name="amount"
                      rules={{
                        required: createOppositeOrderTypeData?.orderType === OrderTypeEnum.BUY,
                      }}
                      render={({ field: { onChange, value } }) => (
                        <Select
                          label={t("common:amount")}
                          withAsterisk
                          data={
                            amountsForOrderData?.allowedAmounts.map((amount) => ({
                              label: amount.toString(),
                              value: amount.toString(),
                            })) ?? []
                          }
                          value={value}
                          onChange={onChange}
                          error={errors.amount && t("validation:required")}
                        />
                      )}
                    />
                  ) : null}
                  <Group align="end">
                    <CTA intent="secondary" onClick={onClose}>
                      {t("actions:cancel")}
                    </CTA>
                    <CTA
                      intent="primary"
                      onClick={guardTradeLock(() => handleSubmit(() => setIsConfirmationModalOpen(true))())}
                    >
                      {createOppositeOrderTypeData?.orderType === OrderTypeEnum.BUY
                        ? t("actions:buy")
                        : t("actions:sell")}
                    </CTA>
                  </Group>
                </Stack>
              </form>
            </ModalCTAs>
          )
        }
      >
        {order && (
          <>
            <AccordionDrawer title={t("product:productType")} initiallyOpen>
              <Grid className={styles.grid}>
                <Grid.Col span={3}>
                  <Text size="sm" color="secondary">
                    {t("common:type")}
                  </Text>
                </Grid.Col>
                <Grid.Col span={9}>
                  <Text size="sm">
                    {user?.language.id && order.salesItem.nameTranslated?.[user.language.id]
                      ? order.salesItem.nameTranslated[user.language.id]
                      : order.salesItem.salesItem}
                  </Text>
                </Grid.Col>

                <Grid.Col span={3}>
                  <Text size="sm" color="secondary">
                    {t("common:price")}
                  </Text>
                </Grid.Col>
                <Grid.Col span={9}>
                  <Text size="sm">
                    <NumberFormatter
                      value={order.priceWithFee?.[
                        currentCompany?.companyCurrency
                          ? currentCompany.companyCurrency.toUpperCase()
                          : order.orderCurrency
                      ]?.toFixed(2)}
                      suffix={` ${order.orderCurrency}/${order.priceUnit.unit}`}
                    />
                  </Text>
                </Grid.Col>

                <Grid.Col span={3}>
                  <Text size="sm" color="secondary">
                    {t("common:remainingAmount")}
                  </Text>
                </Grid.Col>
                <Grid.Col span={9}>
                  <Text size="sm">
                    <NumberFormatter value={order.remainingAmount?.toFixed(2)} suffix={` ${order.unit.unit}`} />
                  </Text>
                </Grid.Col>

                <Grid.Col span={3}>
                  <Text size="sm" color="secondary">
                    {t("common:validFrom")}
                  </Text>
                </Grid.Col>
                <Grid.Col span={9}>
                  <Text size="sm">{order.validFrom ? dayjs(order.validFrom).format("DD.MM.YYYY") : "-"}</Text>
                </Grid.Col>

                <Grid.Col span={3}>
                  <Text size="sm" color="secondary">
                    {t("common:validTo")}
                  </Text>
                </Grid.Col>
                <Grid.Col span={9}>
                  <Text size="sm">{order.validTo ? dayjs(order.validTo).format("DD.MM.YYYY") : "-"}</Text>
                </Grid.Col>
              </Grid>
            </AccordionDrawer>

            {order.salesItem.salesItemProperties?.length > 0 && (
              <AccordionDrawer title={t("common:properties")} initiallyOpen>
                <Grid className={styles.grid}>
                  {[...(order.salesItem.salesItemProperties ?? [])]
                    .sort((a, b) => a.salesItemPropertyId - b.salesItemPropertyId)
                    .map((salesItemProperty) => {
                      const matchingProperty = order.properties?.find(
                        (p) => p.index === salesItemProperty.salesItemPropertyId
                      );
                      const label =
                        salesItemProperty.salesItemPropertyTranslated.find((t) => t.languageId === user?.language.id)
                          ?.propertyTranslated ?? salesItemProperty.salesItemPropertyName;

                      return (
                        <React.Fragment key={salesItemProperty.salesItemPropertyId}>
                          <Grid.Col span={3}>
                            <Text size="sm" color="secondary">
                              {label}
                            </Text>
                          </Grid.Col>
                          <Grid.Col span={9}>
                            <Text size="sm">{matchingProperty?.value ?? "-"}</Text>
                          </Grid.Col>
                        </React.Fragment>
                      );
                    })}
                </Grid>
              </AccordionDrawer>
            )}
          </>
        )}
      </Drawer>

      <Modal
        opened={isConfirmationModalOpen}
        onClose={() => setIsConfirmationModalOpen(false)}
        title={order.orderType.type === OrderTypeEnumString.SELL ? t("actions:buy") : t("actions:sell")}
      >
        <Stack p="xl" gap="md">
          <Text>
            {order.orderType.type === OrderTypeEnumString.SELL
              ? t("orders:market.confirmPurchaseFrom", {
                  seller: order.sellerName,
                })
              : t("orders:market.confirmSaleTo", { buyer: order.sellerName })}
          </Text>
          <Divider />
          <Grid>
            <Grid.Col span={5}>
              <Text size="sm" color="secondary">
                {t("common:type")}
              </Text>
            </Grid.Col>
            <Grid.Col span={7}>
              <Text size="sm">
                {user?.language.id && order.salesItem.nameTranslated?.[user.language.id]
                  ? order.salesItem.nameTranslated[user.language.id]
                  : order.salesItem.salesItem}
              </Text>
            </Grid.Col>

            <Grid.Col span={5}>
              <Text size="sm" color="secondary">
                {t("common:price")}
              </Text>
            </Grid.Col>
            <Grid.Col span={7}>
              <Text size="sm">
                <NumberFormatter
                  value={order.priceWithFee?.[
                    currentCompany?.companyCurrency ? currentCompany.companyCurrency.toUpperCase() : order.orderCurrency
                  ]?.toFixed(2)}
                  suffix={` ${order.orderCurrency}/${order.priceUnit.unit}`}
                />
              </Text>
            </Grid.Col>

            <Grid.Col span={5}>
              <Text size="sm" color="secondary">
                {t("common:remainingAmount")}
              </Text>
            </Grid.Col>
            <Grid.Col span={7}>
              <Text size="sm">
                <NumberFormatter value={order.remainingAmount?.toFixed(2)} suffix={` ${order.unit.unit}`} />
              </Text>
            </Grid.Col>

            <Grid.Col span={5}>
              <Text size="sm" color="secondary">
                {t("orders:totalPrice")}
              </Text>
            </Grid.Col>
            <Grid.Col span={7}>
              <Text size="sm">
                <NumberFormatter
                  value={(
                    order.remainingAmount *
                    (order.priceWithFee?.[
                      currentCompany?.companyCurrency
                        ? currentCompany.companyCurrency.toUpperCase()
                        : order.orderCurrency
                    ] ?? 0)
                  )?.toFixed(2)}
                  suffix={` ${order.orderCurrency}`}
                />
              </Text>
            </Grid.Col>

            {/* //TODO: MAYBE MAKE THIS REUSABLE, USED IN 3 PLACES NOW */}
            {[...(order.salesItem.salesItemProperties ?? [])]
              .sort((a, b) => a.salesItemPropertyId - b.salesItemPropertyId)
              .map((salesItemProperty) => {
                const matchingProperty = order.properties?.find(
                  (p) => p.index === salesItemProperty.salesItemPropertyId
                );
                const label =
                  salesItemProperty.salesItemPropertyTranslated.find((tr) => tr.languageId === user?.language.id)
                    ?.propertyTranslated ?? salesItemProperty.salesItemPropertyName;
                return (
                  <React.Fragment key={salesItemProperty.salesItemPropertyId}>
                    <Grid.Col span={5}>
                      <Text size="sm" color="secondary">
                        {label}
                      </Text>
                    </Grid.Col>
                    <Grid.Col span={7}>
                      <Text size="sm">{matchingProperty?.value ?? "-"}</Text>
                    </Grid.Col>
                  </React.Fragment>
                );
              })}
          </Grid>
        </Stack>
        <ModalCTAs>
          <CTA intent="secondary" onClick={() => setIsConfirmationModalOpen(false)}>
            {t("actions:cancel")}
          </CTA>
          <CTA intent="primary" onClick={() => handleSellOrPurchase()}>
            {t("actions:accept")}
          </CTA>
        </ModalCTAs>
      </Modal>
    </>
  );
};

export default OrderDrawer;
